import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../../interceptors/adminAxios';
import CalendarPicker from '../ds/reports/CalendarPicker';
import { useConfirm } from '../../hooks/useConfirm';

interface Log {
  id: number;
  device_id: string;
  category: string;
  level: string;
  message: string;
  created_at: string;
  display_name?: string | null;
  company_id?: string | null;
}

interface Company { id: number; name: string; display_name: string; }

const glassCard: React.CSSProperties = {
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid var(--glass-border)',
  borderRadius: 14,
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 13px', borderRadius: 8,
  border: '1px solid var(--glass-border)',
  background: 'transparent', color: 'var(--text-secondary)',
  fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};

const selectSt: React.CSSProperties = {
  background: 'var(--input-bg)', border: '1px solid var(--input-border)',
  borderRadius: 8, padding: '5px 10px', fontSize: 12, color: 'var(--text-primary)',
  fontFamily: 'inherit', outline: 'none', cursor: 'pointer',
};

const LEVEL_COLORS: Record<string, { color: string; bg: string }> = {
  info:    { color: '#60a5fa', bg: 'rgba(96,165,250,.12)'  },
  warning: { color: '#fbbf24', bg: 'rgba(251,191,36,.12)'  },
  error:   { color: '#f87171', bg: 'rgba(248,113,113,.12)' },
  debug:   { color: '#94a3b8', bg: 'rgba(148,163,184,.12)' },
};

const CATEGORIES = ['', 'command', 'status', 'heartbeat', 'error', 'sync', 'auth'];
const LEVELS     = ['', 'info', 'warning', 'error', 'debug'];

const AdminLogsPage: React.FC = () => {
  const { confirm, confirmModal } = useConfirm();
  const [logs, setLogs]           = useState<Log[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [company, setCompany]     = useState('');
  const [level, setLevel]         = useState('');
  const [category, setCategory]   = useState('');
  const [deviceId, setDeviceId]   = useState('');
  const [dateFrom, setDateFrom]   = useState<Date | null>(null);
  const [dateTo, setDateTo]       = useState<Date | null>(null);
  const [loading, setLoading]     = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    axios.get<Company[]>('/admin/companies').then(r => setCompanies(r.data)).catch(() => {});
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (company)  params.company   = company;
      if (level)    params.level     = level;
      if (category) params.category  = category;
      if (deviceId) params.device_id = deviceId;
      if (dateFrom) {
        const d = new Date(dateFrom);
        d.setHours(0, 0, 0, 0);
        params.date_from = d.toISOString();
      }
      if (dateTo) {
        const d = new Date(dateTo);
        d.setHours(23, 59, 59, 999);
        params.date_to = d.toISOString();
      }
      const { data } = await axios.get<Log[]>('/admin/logs', { params });
      setLogs(data);
    } catch {} finally { setLoading(false); }
  }, [company, level, category, deviceId, dateFrom, dateTo]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  useEffect(() => {
    if (autoRefresh) {
      timerRef.current = setInterval(() => loadLogs(), 5000);
    } else {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, loadLogs]);

  const handleClearLogs = async () => {
    const title = company
      ? `Clear all logs for company "${company}"?`
      : 'Clear ALL logs?';
    const message = company ? undefined : 'This cannot be undone.';
    if (!await confirm({ title, message, confirmLabel: 'Clear', cancelLabel: 'Cancel', variant: 'danger' })) return;
    try {
      const params: Record<string, string> = {};
      if (company) params.company = company;
      await axios.delete('/admin/logs', { params });
      loadLogs();
    } catch {}
  };

  const fmt = (d: string) => {
    const dt = new Date(d);
    return dt.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const COLS = ['Time', 'Level', 'Category', 'Device ID', 'Device', 'Company', 'Message'];

  return (
    <>
      {/* Topbar */}
      <div style={{ height: 50, flexShrink: 0, background: 'var(--topbar-bg)', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 8 }}>
        <div style={{ fontSize: 15, fontWeight: 700, flex: 1, color: 'var(--text-primary)' }}>Logs</div>
        <button style={{ ...btnGhost, padding: '5px 10px', borderColor: autoRefresh ? 'var(--accent)' : 'var(--glass-border)', color: autoRefresh ? 'var(--accent-light)' : 'var(--text-secondary)' }}
          onClick={() => setAutoRefresh(p => !p)}>
          {autoRefresh ? '⏸ Auto' : '▶ Auto'}
        </button>
        <button style={btnGhost} onClick={() => loadLogs()}>↻ Refresh</button>
        <button style={{ ...btnGhost, color: '#f87171', borderColor: 'rgba(248,113,113,.25)' }} onClick={handleClearLogs}>✕ Clear</button>
      </div>

      {/* Filters */}
      <div style={{ padding: '10px 16px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid var(--divider)', background: 'var(--topbar-bg)' }}>
        <select value={company} onChange={e => setCompany(e.target.value)} style={selectSt}>
          <option value="">All companies</option>
          {companies.map(c => <option key={c.id} value={c.name}>{c.display_name || c.name}</option>)}
        </select>
        <select value={level} onChange={e => setLevel(e.target.value)} style={selectSt}>
          <option value="">All levels</option>
          {LEVELS.filter(Boolean).map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
        </select>
        <select value={category} onChange={e => setCategory(e.target.value)} style={selectSt}>
          <option value="">All categories</option>
          {CATEGORIES.filter(Boolean).map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input value={deviceId} onChange={e => setDeviceId(e.target.value)} placeholder="Device ID…"
          style={{ ...selectSt, width: 110 }} />

        {/* Date range picker */}
        <CalendarPicker
          mode="range"
          startDate={dateFrom}
          endDate={dateTo}
          onChange={(start, end) => { setDateFrom(start); setDateTo(end); }}
          clearable
          placeholder="Date range"
        />

        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-disabled)', alignSelf: 'center', fontVariantNumeric: 'tabular-nums' }}>
          {loading ? 'Loading…' : `${logs.length} entries`}
        </div>
      </div>

      {/* Logs table */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
        <div style={{ ...glassCard, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--glass-hover)', borderBottom: '1px solid var(--divider)' }}>
                {COLS.map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-disabled)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr>
                  <td colSpan={COLS.length} style={{ padding: '20px 12px', textAlign: 'center', fontSize: 13, color: 'var(--text-disabled)' }}>No logs found</td>
                </tr>
              )}
              {logs.map((log, i) => {
                const lc = LEVEL_COLORS[log.level] ?? { color: '#94a3b8', bg: 'rgba(148,163,184,.1)' };
                return (
                  <tr key={log.id} style={{ borderBottom: i < logs.length - 1 ? '1px solid var(--divider)' : 'none', transition: 'background .1s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--row-hover)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    <td style={{ padding: '6px 12px', fontSize: 11, color: 'var(--text-disabled)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontFamily: 'JetBrains Mono, monospace' }}>{fmt(log.created_at)}</td>
                    <td style={{ padding: '6px 12px' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: lc.bg, color: lc.color }}>{log.level}</span>
                    </td>
                    <td style={{ padding: '6px 12px', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{log.category}</td>
                    <td style={{ padding: '6px 12px', fontSize: 11, fontFamily: 'JetBrains Mono, monospace', color: 'var(--accent-light)', whiteSpace: 'nowrap' }}>{log.device_id}</td>
                    <td style={{ padding: '6px 12px', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{log.display_name ?? '—'}</td>
                    <td style={{ padding: '6px 12px', fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{log.company_id ?? '—'}</td>
                    <td style={{ padding: '6px 12px', fontSize: 12, color: 'var(--text-primary)', maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.message}>{log.message}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {confirmModal}
    </>
  );
};

export default AdminLogsPage;
